// Basic
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
// Store & actions
import actionsContracts from '@/app/modules/contracts/api/actionsContracts';
import useContractStore from '@/app/store/modules/contract';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import { Modal, ModalContent } from '@/common-utils/components/ui/modal';
import BodySelectPaymentMethod from './components/BodySelectPaymentMethod/BodySelectPaymentMethod';
import BodyAddContractForm from './components/BodyAddContractForm/BodyAddContractForm';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import Entity from '@/common-utils/components/renders/entity/Entity';
// Common
import EXTERNAL_CONTRACT_TYPES from '../../../enums/externalContractType';
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsMerchantUser } from '@/app/i18n/namespaces';

/**
 * @param {boolean} isOpen element that triggers modal opening
 * @param {function} onClose element that triggers modal opening
 * @returns {JSX} ModalAddExternalContract
 */
function ModalAddExternalContract({ isOpen, setIsOpen, merchantAccountDetails }) {
    // Constants
    const STEPS_NUMBER = 2;

    // Url params
    const { contractAccessId: contractsAccessId } = useParams();

    // Toast
    const { toast } = useToast();

    // Store
    const { storeRefetchContracts } = useContractStore();

    // State
    const [step, setStep] = useState(0);
    const [selectedExtContract, setSelectedExtContract] = useState({});
    const [validForm, setValidForm] = useState(false);
    const [formData, setFormData] = useState(false);

    // Api Calls
    const onSuccess = () => {
        const entityName = selectedExtContract?.type?.name?.toLowerCase();
        storeRefetchContracts(true);
        setIsOpen(false);
        toast({
            description: <Entity namespace={nsMerchantUser} entity={`modal.add_external_contract.${entityName}.success`} />,
            variant: 'success'
        });
    };
    const onError = (error) => {
        const entityName = selectedExtContract?.type?.name?.toLowerCase();
        const errorCode = error?.response?.data?.errorCode ?? error?.code ?? 'UNKOWN_ERROR';
        setIsOpen(false);
        toast({
            description: <Entity namespace={nsMerchantUser} entity={`modal.add_external_contract.${entityName}.errors.${errorCode}`} />,
            variant: 'danger'
        });
    };
    const addContractByApiKey = actionsContracts.addContractByApiKey(onSuccess, onError);
    const addVadContract = actionsContracts.addVadContract(onSuccess, onError);

    // Functions
    const getSelectedExternalContract = (externalContract) => {
        setSelectedExtContract({ ...selectedExtContract, type: externalContract.type ?? '' });
    };

    const getHeaderTitleProps = () => {
        if (step === 0) {
            return {
                title: 'modal.add_external_contract.title'
            };
        }
        if (selectedExtContract.type?.name === EXTERNAL_CONTRACT_TYPES.ALMA.name) {
            return {
                title: 'modal.add_external_contract.title1',
                data: { externalContractName: selectedExtContract.type.name }
            };
        }
        else {
            return {
                title: 'modal.add_external_contract.title2',
                data: { merchantName: merchantAccountDetails.label }
            };
        }
    };

    const handleSumbit = () => {
        if (selectedExtContract.type.name === EXTERNAL_CONTRACT_TYPES.ALMA.name) {
            const { apiKey, labelTicketTpe } = formData;
            addContractByApiKey.mutate({
                apiKey,
                labelTicketTpe,
                contractsAccessId,
                application: selectedExtContract.type.name
            });
        }
        else {
            const { bankCode, contractLabel, contractType, contractNumber: merchantId, x25Address, rank } = formData;
            addVadContract.mutate({
                contractsAccessId,
                contractLabel,
                application: contractType,
                bankCode,
                merchantId,
                x25Address,
                logicalNumber: rank
            });
        }
    };

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className='max-w-xl'>
                <HeaderTitle
                    title={getHeaderTitleProps().title}
                    data={getHeaderTitleProps().data}
                    namespace={nsMerchantUser}
                />
                {step === 0 && <BodySelectPaymentMethod getSelectedExternalContract={getSelectedExternalContract} />}
                {step === 1 && <BodyAddContractForm setValidForm={setValidForm} setFormData={setFormData} selectedExtContract={selectedExtContract} />}
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    onValidate={handleSumbit}
                    isLoading={addContractByApiKey.isLoading || addVadContract.isLoading}
                    disableValidation={!validForm && step === 1}
                />
            </ModalContent>
        </Modal>
    );
}

ModalAddExternalContract.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    merchantAccountDetails: PropTypes.object
};

export default ModalAddExternalContract;